import { utcDateKey } from "../nonogram/daily";
import type { Puzzle } from "../nonogram/types";
import type { SeoPage } from "./pages";

/**
 * A page per published daily date.
 *
 * Eleven fixed URLs is a small surface for a site whose whole premise is a new puzzle
 * every day. Every date already has a stable, uniquely solvable grid — it just had no
 * address of its own, so a month of work was reachable only through a calendar widget
 * that a crawler cannot operate.
 *
 * These are not the same page with the date swapped: each one prints its own clue
 * lists, which is what the puzzle actually is. The picture's name stays out, here as
 * everywhere else — it is the reward for solving the grid, not metadata.
 */

const DAILY_EPOCH_KEY = "2026-01-01";
/** Public archive window: calendar, date list, and sitemap all use this length. */
export const ARCHIVE_DAYS = 30;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function isPublishedDailyDate(dateKey: string, today = utcDateKey()): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return false;
  const timestamp = Date.parse(`${dateKey}T00:00:00.000Z`);
  if (!Number.isFinite(timestamp)) return false;
  // Round-trips only for real calendar days: 2026-02-31 parses and is not a date.
  if (new Date(timestamp).toISOString().slice(0, 10) !== dateKey) return false;
  return dateKey >= DAILY_EPOCH_KEY && dateKey <= today;
}

export function dailyDatePath(dateKey: string): string {
  return `/daily-nonogram/${dateKey}`;
}

function longDate(dateKey: string): string {
  const date = new Date(`${dateKey}T00:00:00.000Z`);
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

function weekdayName(dateKey: string): string {
  return WEEKDAYS[new Date(`${dateKey}T00:00:00.000Z`).getUTCDay()] ?? "";
}

function clueList(clues: readonly (readonly number[])[]): string {
  return clues.map((line) => (line.length ? line.join(" ") : "0")).join(" · ");
}

/** The last `count` published dates, newest first. */
export function recentDailyDates(count: number, today = utcDateKey()): string[] {
  const dates: string[] = [];
  const cursor = new Date(`${today}T00:00:00.000Z`);
  for (let offset = 0; offset < count; offset += 1) {
    const key = cursor.toISOString().slice(0, 10);
    if (!isPublishedDailyDate(key, today)) break;
    dates.push(key);
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }
  return dates;
}

/**
 * Built from a puzzle that is handed in rather than looked up, so the browser can
 * render the identical page for the grid it already fetched without pulling the whole
 * catalogue into the bundle.
 */
export function dailyDatePageFor(puzzle: Puzzle, dateKey: string, today = utcDateKey()): SeoPage {
  const size = `${puzzle.size}×${puzzle.size}`;
  const difficulty = puzzle.difficulty;
  const readable = longDate(dateKey);
  const weekday = weekdayName(dateKey);
  const isToday = dateKey === today;

  return {
    path: dailyDatePath(dateKey),
    ogImage: "/og/daily.png",
    ogImageAlt: `Nonogram Now — the daily nonogram grid for ${readable}`,
    title: `Daily Nonogram · ${readable} — Free ${size} puzzle`,
    description: `Play the ${size} daily nonogram published on ${readable}. Uniquely solvable, free, and playable in the browser without an account.`,
    eyebrow: isToday ? "Today’s puzzle" : "From the archive",
    heading: `Daily Nonogram · ${readable}`,
    lede: `The ${weekday} grid for ${readable}: a ${size} ${difficulty} puzzle, exactly as it was published.`,
    relatedPaths: ["/daily-nonogram/archive", "/", `/nonogram/${difficulty === "medium" ? "" : difficulty}`.replace(/\/$/, ""), "/how-to-play-nonogram"],
    sections: [
      {
        heading: "The clues for this grid",
        paragraphs: [
          `Rows, top to bottom: ${clueList(puzzle.rowClues)}.`,
          `Columns, left to right: ${clueList(puzzle.columnClues)}.`,
          "Each number is the length of one unbroken run of filled squares in that line, in order, with at least one empty square between runs.",
        ],
      },
      {
        heading: `Why ${readable} is a ${size}`,
        paragraphs: [
          `The size follows the day of the week: Sunday is a quick 5×5, Saturday a longer 15×15, and every other day a balanced 10×10. ${weekday} makes this one a ${size} ${difficulty} grid.`,
          "The date fixes the puzzle. Opening this page next year opens the same grid, with the same single solution, on any device.",
        ],
      },
    ],
    faqs: [
      {
        question: `Can I still play the nonogram from ${readable}?`,
        answer: `Yes. Every published date stays open, and it is always the same grid — this page loads the ${size} puzzle from ${readable} exactly as it was published.`,
      },
      {
        question: "Does replaying an old date change my streak?",
        answer: "No. The streak counts days you completed the puzzle on the day itself; the archive is for catching up on a picture you missed, not for repairing a streak.",
      },
    ],
  };
}
